/**
 * AudioPitchShifter - Web Audio API wrapper for real-time pitch shifting
 * Integrates with our existing dual-track karaoke system
 */

const PITCH_SHIFT_ENGINE_BYPASS = 'bypass';
const PITCH_SHIFT_ENGINE_SIGNALSMITH = 'signalsmith';
const AUDIO_SAMPLE_RATE = 44100;
const SIGNALSMITH_MODULE_URL = '/audio-worklets/signalsmith-stretch/SignalsmithStretch.mjs?v=production-1';
const SIGNALSMITH_TONALITY_HZ = 8000;
const MASTER_GAIN_DB = -9;
const FALLBACK_MASTER_GAIN_DB = -12;

class AudioPitchShifter {
  constructor(pitchShiftEngine = PITCH_SHIFT_ENGINE_SIGNALSMITH) {
    this.audioContext = null;
    this.vocalGainNode = null;
    this.accompanimentGainNode = null;
    this.drumsGainNode = null;
    this.sharedPitchNode = null;
    this.drumsDelayNode = null;
    this.masterInputNode = null;
    this.masterGainNode = null;
    this.masterLimiterNode = null;
    this.vocalSourceNode = null;
    this.accompanimentSourceNode = null;
    this.drumsSourceNode = null;
    this.isInitialized = false;
    this.currentPitchShift = 0; // In semitones
    this.pitchShiftEngine = [
      PITCH_SHIFT_ENGINE_BYPASS,
      PITCH_SHIFT_ENGINE_SIGNALSMITH,
    ].includes(pitchShiftEngine) ? pitchShiftEngine : PITCH_SHIFT_ENGINE_SIGNALSMITH;
    this.availablePitchShiftEngines = new Set([PITCH_SHIFT_ENGINE_BYPASS]);
    this.pitchNodeSets = {
      [PITCH_SHIFT_ENGINE_BYPASS]: null,
      [PITCH_SHIFT_ENGINE_SIGNALSMITH]: null,
    };
    this.signalsmithFactory = null;
    this.signalsmithLatencySeconds = 0;

    // Track URLs for recreating source nodes
    this.lastVocalUrl = null;
    this.lastAccompanimentUrl = null;
    this.lastDrumsUrl = null;

    // Store original buffers for VexWarp processing
    this.originalVocalBuffer = null;
    this.originalAccompanimentBuffer = null;
    this.originalDrumsBuffer = null;

    // Time tracking for progress bar
    this.startTime = 0;
    this.pauseTime = 0;
    this.seekOffset = 0;
    this.isPlaying = false;
    this.timeUpdateCallback = null;
    this.timeUpdateInterval = null;

    // Track song completion
    this.songEndedCallback = null;
    this.vocalEnded = false;
    this.accompanimentEnded = false;
    this.drumsEnded = false;
    this.songDuration = 0;
    this.songEndHandled = false;

    // Track if we're manually stopping (for pause) vs natural end
    this.isManualStop = false;
  }

  async initialize() {
    try {
      // Karaoke stems are produced at 44.1 kHz. Requesting that rate also keeps
      // Signalsmith's block and latency configuration consistent across devices.
      const contextOptions = { sampleRate: AUDIO_SAMPLE_RATE };

      try {
        this.audioContext = new (window.AudioContext || window.webkitAudioContext)(contextOptions);
      } catch (error) {
        this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
      }

      console.log('🎵 Audio context created:', this.audioContext.state, 'Sample Rate:', this.audioContext.sampleRate);

      this.vocalGainNode = this.audioContext.createGain();
      this.accompanimentGainNode = this.audioContext.createGain();
      this.drumsGainNode = this.audioContext.createGain();
      console.log('🎵 Gain nodes created');

      if (!this.audioContext.audioWorklet) {
        console.warn('🎵 AudioWorklet unavailable; using raw playback');
        this.pitchShiftEngine = PITCH_SHIFT_ENGINE_BYPASS;
        this.createMasterOutput(false);
      } else {
        try {
          await this.audioContext.audioWorklet.addModule('/audio-worklets/master-lookahead-limiter.js');
          this.createMasterOutput(true);
        } catch (error) {
          console.warn('🎵 Master limiter unavailable; using conservative output gain:', error);
          this.createMasterOutput(false);
        }

        try {
          // Load both the main-thread factory and AudioWorklet module from the
          // same-origin URL. Production CSP intentionally blocks blob: scripts,
          // and Safari reports the blocked blob import as an unavailable engine.
          this.signalsmithFactory = await this.loadSignalsmithFactory();
          await this.createSignalsmithPitchNodeSet();
          this.availablePitchShiftEngines.add(PITCH_SHIFT_ENGINE_SIGNALSMITH);
          console.log('🎵 Signalsmith Stretch AudioWorklet loaded successfully');
        } catch (error) {
          console.warn('🎵 Signalsmith Stretch is unavailable; using raw playback:', error);
          this.pitchShiftEngine = PITCH_SHIFT_ENGINE_BYPASS;
        }
      }

      this.activatePitchNodeSet(this.pitchShiftEngine);
      console.log(`🎵 Real-time audio graph connected (${this.pitchShiftEngine})`);

      this.isInitialized = true;
      console.log('🎵 AudioPitchShifter initialized successfully');

      return true;
    } catch (error) {
      console.error('Failed to initialize AudioPitchShifter:', error);
      return false;
    }
  }

  async loadSignalsmithFactory(
    loadModule = (url) => import(/* @vite-ignore */ url)
  ) {
    const module = await loadModule(SIGNALSMITH_MODULE_URL);
    if (typeof module.default !== 'function') {
      throw new Error('Signalsmith module did not export a factory');
    }
    module.default.moduleUrl = SIGNALSMITH_MODULE_URL;
    return module.default;
  }

  createMasterOutput(useLimiter) {
    this.masterInputNode = this.audioContext.createGain();
    this.masterGainNode = this.audioContext.createGain();
    this.masterGainNode.gain.value = Math.pow(
      10,
      (useLimiter ? MASTER_GAIN_DB : FALLBACK_MASTER_GAIN_DB) / 20
    );
    this.masterInputNode.connect(this.masterGainNode);

    if (useLimiter) {
      this.masterLimiterNode = new AudioWorkletNode(
        this.audioContext,
        'master-lookahead-limiter',
        {
          numberOfInputs: 1,
          numberOfOutputs: 1,
          channelCount: 2,
          channelCountMode: 'explicit',
          outputChannelCount: [2],
        }
      );
      this.masterGainNode.connect(this.masterLimiterNode);
      this.masterLimiterNode.connect(this.audioContext.destination);
    } else {
      this.masterGainNode.connect(this.audioContext.destination);
    }
  }

  async createSignalsmithPitchNodeSet() {
    if (!this.signalsmithFactory) {
      throw new Error('Signalsmith Stretch module is unavailable');
    }

    const shared = await this.signalsmithFactory(this.audioContext, {
      numberOfInputs: 1,
      numberOfOutputs: 1,
      channelCount: 2,
      channelCountMode: 'explicit',
      outputChannelCount: [2],
    });
    await shared.configure({ preset: 'default', splitComputation: true });
    this.signalsmithLatencySeconds = await shared.latency();

    const drumsDelay = this.audioContext.createDelay(1);
    drumsDelay.delayTime.value = this.signalsmithLatencySeconds;
    this.pitchNodeSets[PITCH_SHIFT_ENGINE_SIGNALSMITH] = { shared, drumsDelay };
  }

  ensurePitchNodeSet(engine) {
    if (this.pitchNodeSets[engine]) return this.pitchNodeSets[engine];

    if (engine === PITCH_SHIFT_ENGINE_BYPASS) {
      this.pitchNodeSets[engine] = { mixer: this.audioContext.createGain() };
      return this.pitchNodeSets[engine];
    }

    throw new Error(`Pitch shift engine is not initialized: ${engine}`);
  }

  disconnectPitchNodeSet(engine) {
    const nodes = this.pitchNodeSets[engine];
    if (!nodes) return;
    for (const node of Object.values(nodes)) {
      try {
        node.disconnect();
      } catch {
        // A disconnected AudioNode is already in the desired state.
      }
    }
  }

  disposePitchNodeSet(engine) {
    const nodes = this.pitchNodeSets[engine];
    if (!nodes) return;

    if (engine === PITCH_SHIFT_ENGINE_SIGNALSMITH) {
      nodes.shared.stop?.().catch?.(() => {});
    }
    this.disconnectPitchNodeSet(engine);
    this.pitchNodeSets[engine] = null;
  }

  disconnectGainOutputs() {
    for (const gainNode of [this.vocalGainNode, this.accompanimentGainNode, this.drumsGainNode]) {
      try {
        gainNode?.disconnect();
      } catch {
        // A disconnected gain is already ready to be rewired.
      }
    }
  }

  activatePitchNodeSet(engine) {
    const nodes = this.ensurePitchNodeSet(engine);

    this.disconnectPitchNodeSet(this.pitchShiftEngine);
    this.disconnectGainOutputs();
    this.pitchShiftEngine = engine;

    if (engine === PITCH_SHIFT_ENGINE_BYPASS) {
      this.sharedPitchNode = null;
      this.drumsDelayNode = null;
      this.vocalPitchNode = null;
      this.accompanimentPitchNode = null;
      this.drumsPitchNode = null;

      this.vocalGainNode.connect(nodes.mixer);
      this.accompanimentGainNode.connect(nodes.mixer);
      this.drumsGainNode.connect(nodes.mixer);
      nodes.mixer.connect(this.masterInputNode);
      return;
    }

    this.sharedPitchNode = nodes.shared;
    this.drumsDelayNode = nodes.drumsDelay;
    this.vocalPitchNode = nodes.shared;
    this.accompanimentPitchNode = nodes.shared;
    this.drumsPitchNode = null;

    this.vocalGainNode.connect(nodes.shared);
    this.accompanimentGainNode.connect(nodes.shared);
    this.drumsGainNode.connect(nodes.drumsDelay);
    nodes.shared.connect(this.masterInputNode);
    nodes.drumsDelay.connect(this.masterInputNode);
  }

  async setPitchShiftEngine(engine) {
    if (![PITCH_SHIFT_ENGINE_BYPASS, PITCH_SHIFT_ENGINE_SIGNALSMITH].includes(engine)) {
      throw new Error(`Unknown pitch shift engine: ${engine}`);
    }
    if (!this.availablePitchShiftEngines.has(engine)) {
      throw new Error(`Pitch shift engine is unavailable: ${engine}`);
    }
    if (engine === this.pitchShiftEngine) return true;

    const position = this.getCurrentTime();
    const shouldResume = this.isPlaying;
    const hasLoadedAudio = Boolean(
      this.originalVocalBuffer || this.originalAccompanimentBuffer || this.originalDrumsBuffer
    );

    this.stop();
    this.activatePitchNodeSet(engine);
    this.setPitchShift(this.currentPitchShift);

    if (hasLoadedAudio) {
      await this.seek(
        position,
        this.lastVocalUrl,
        this.lastAccompanimentUrl,
        this.lastDrumsUrl,
        shouldResume
      );
    }

    console.log(`🎵 Pitch shift engine changed to ${engine}`);
    return true;
  }

  getPitchShiftEngine() {
    return this.pitchShiftEngine;
  }

  async prepareSignalsmithForPlayback() {
    if (
      this.pitchShiftEngine !== PITCH_SHIFT_ENGINE_SIGNALSMITH ||
      !this.sharedPitchNode
    ) {
      return;
    }

    await this.sharedPitchNode.configure({
      preset: 'default',
      splitComputation: true,
    });
    await this.sharedPitchNode.schedule({
      output: this.audioContext.currentTime,
      active: true,
      semitones: this.currentPitchShift,
      tonalityHz: SIGNALSMITH_TONALITY_HZ,
      formantSemitones: 0,
      formantCompensation: false,
    });
    this.masterLimiterNode?.port?.postMessage({ type: 'reset' });
  }

  setPitchShift(semitones) {
    if (!this.isInitialized) {
      console.warn('AudioPitchShifter not initialized');
      return;
    }

    this.currentPitchShift = semitones;

    if (this.pitchShiftEngine === PITCH_SHIFT_ENGINE_BYPASS) {
      console.log(`🎵 Pitch shifting bypassed (${semitones} semitones stored)`);
      return;
    }

    console.log(`🎵 Setting Signalsmith pitch shift: ${semitones} semitones`);
    this.sharedPitchNode?.schedule({
      output: this.audioContext.currentTime,
      semitones,
      tonalityHz: SIGNALSMITH_TONALITY_HZ,
      formantSemitones: 0,
      formantCompensation: false,
    }).catch((error) => {
      console.error('🎵 Failed to schedule Signalsmith pitch shift:', error);
    });
  }

  // Set volume for vocal track (0.0 to 2.0, where 1.0 = original volume, 2.0 = double volume)
  setVocalVolume(volume) {
    if (this.vocalGainNode) {
      this.vocalGainNode.gain.value = volume;
    }
  }

  // Set volume for accompaniment track (0.0 to 2.0, where 1.0 = original volume, 2.0 = double volume)
  setAccompanimentVolume(volume) {
    if (this.accompanimentGainNode) {
      this.accompanimentGainNode.gain.value = volume;
    }
  }

  // Set volume for drums track (0.0 to 2.0, where 1.0 = original volume, 2.0 = double volume)
  setDrumsVolume(volume) {
    if (this.drumsGainNode) {
      this.drumsGainNode.gain.value = volume;
    }
  }

  // Check if AudioContext is suspended and needs user gesture
  isAudioContextSuspended() {
    return this.audioContext && this.audioContext.state === 'suspended';
  }

  // Resume AudioContext after user gesture
  async resumeAudioContext() {
    if (this.audioContext && this.audioContext.state === 'suspended') {
      console.log('🎵 Resuming AudioContext after user gesture...');
      try {
        await this.audioContext.resume();
        console.log('🎵 AudioContext resumed successfully');
        return true;
      } catch (error) {
        console.error('🎵 Failed to resume AudioContext:', error);
        return false;
      }
    }
    return true; // Already running or not suspended
  }

  /** Connection target for a new BufferSource. Per-track gain precedes routing. */
  getTrackTargetNode(trackType) {
    if (trackType === 'vocal') return this.vocalGainNode;
    if (trackType === 'accompaniment') return this.accompanimentGainNode;
    if (trackType === 'drums') return this.drumsGainNode;
    throw new Error(`Unknown track type: ${trackType}`);
  }

  _teardownSourceNode(node) {
    if (!node) return;
    try {
      node.onended = null;
      node.stop();
    } catch (e) {
      // Already stopped
    }
    try {
      node.disconnect();
    } catch (e) {
      // Node may already be disconnected
    }
  }

  // Load and process audio from URL with VexWarp
  // Fast source creation from cached buffer (for seeking)
  createSourceFromBuffer(buffer, trackType = 'vocal') {
    if (!this.isInitialized || !buffer) {
      throw new Error('AudioPitchShifter not initialized or buffer not provided');
    }

    // Create source node from cached buffer (no network request!)
    const sourceNode = this.audioContext.createBufferSource();
    sourceNode.buffer = buffer;

    if (trackType === 'vocal') {
      if (this.vocalSourceNode) {
        this._teardownSourceNode(this.vocalSourceNode);
      }

      sourceNode.connect(this.getTrackTargetNode('vocal'));
      this.vocalSourceNode = sourceNode;
      console.log('🎵 ⚡ Vocal track connected from cached buffer');
    } else if (trackType === 'accompaniment') {
      if (this.accompanimentSourceNode) {
        this._teardownSourceNode(this.accompanimentSourceNode);
      }

      sourceNode.connect(this.getTrackTargetNode('accompaniment'));
      this.accompanimentSourceNode = sourceNode;
      console.log('🎵 ⚡ Accompaniment track connected from cached buffer');
    } else if (trackType === 'drums') {
      if (this.drumsSourceNode) {
        this._teardownSourceNode(this.drumsSourceNode);
      }

      sourceNode.connect(this.getTrackTargetNode('drums'));
      this.drumsSourceNode = sourceNode;
      console.log('🎵 ⚡ Drums track connected from cached buffer');
    }

    return sourceNode;
  }

  async loadAudioFromUrl(url, trackType = 'vocal') {
    if (!this.isInitialized) {
      throw new Error('AudioPitchShifter not initialized');
    }

    try {
      // Check if AudioContext is suspended
      if (this.isAudioContextSuspended()) {
        throw new Error('AudioContext is suspended - user gesture required');
      }

      // Resume audio context if suspended
      if (this.audioContext.state === 'suspended') {
        await this.audioContext.resume();
      }

      // Fetch and decode audio
      const response = await fetch(url);
      const arrayBuffer = await response.arrayBuffer();
      const originalBuffer = await this.audioContext.decodeAudioData(arrayBuffer);

      // Store original buffer for processing
      if (trackType === 'vocal') {
        this.lastVocalUrl = url;
        this.originalVocalBuffer = originalBuffer;
      } else if (trackType === 'accompaniment') {
        this.lastAccompanimentUrl = url;
        this.originalAccompanimentBuffer = originalBuffer;
      } else if (trackType === 'drums') {
        this.lastDrumsUrl = url;
        this.originalDrumsBuffer = originalBuffer;
      }

      // Use the new createSourceFromBuffer method
      return this.createSourceFromBuffer(originalBuffer, trackType);
    } catch (error) {
      console.error(`Failed to load audio from ${url}:`, error);
      throw error;
    }
  }

  // Load audio from pre-decoded buffer (cache hit path)
  loadAudioFromBuffer(buffer, trackType = 'vocal', sourceUrl = null) {
    if (!this.isInitialized) {
      throw new Error('AudioPitchShifter not initialized');
    }

    if (!buffer) {
      throw new Error('Audio buffer is required');
    }

    console.log(`🎵 ⚡ FAST: Loading ${trackType} from cached buffer`);

    // Store buffer and URL for processing
    if (trackType === 'vocal') {
      this.originalVocalBuffer = buffer;
      this.lastVocalUrl = sourceUrl || `cached-vocal-${Date.now()}`;
    } else if (trackType === 'accompaniment') {
      this.originalAccompanimentBuffer = buffer;
      this.lastAccompanimentUrl = sourceUrl || `cached-accompaniment-${Date.now()}`;
    } else if (trackType === 'drums') {
      this.originalDrumsBuffer = buffer;
      this.lastDrumsUrl = sourceUrl || `cached-drums-${Date.now()}`;
    }

    // Create source node from buffer (no network request!)
    return this.createSourceFromBuffer(buffer, trackType);
  }

  // Play both tracks (recreate source nodes if needed)
  async play() {
    console.log('🎵 Play requested - checking source nodes');

    // Ensure audio context is running
    if (this.audioContext.state === 'suspended') {
      await this.audioContext.resume();
      console.log('🎵 Audio context resumed');
    }

    // Stop any existing playback first
    this.stop();

    // Minimal delay to ensure cleanup is complete (reduced from 50ms to 10ms)
    await new Promise(resolve => setTimeout(resolve, 10));
    await this.prepareSignalsmithForPlayback();

    // Reset completion tracking and manual stop flag
    this.vocalEnded = false;
    this.accompanimentEnded = false;
    this.drumsEnded = false;
    this.isManualStop = false;
    this.songEndHandled = false;

    // Create source nodes from cached buffers (much faster!)
    console.log('🎵 ⚡ Creating source nodes from cached buffers');
    let vocalSource = null;
    let accompanimentSource = null;
    let drumsSource = null;

    // Use cached buffers if available (FAST), otherwise load from URL (SLOW)
    if (this.lastVocalUrl) {
      if (this.originalVocalBuffer) {
        vocalSource = this.createSourceFromBuffer(this.originalVocalBuffer, 'vocal');
        console.log('🎵 ⚡ FAST: Using cached vocal buffer for play');
      } else {
        console.log('🎵 ⚠️ SLOW: Loading vocal from URL for play (buffer not cached)');
        vocalSource = await this.loadAudioFromUrl(this.lastVocalUrl, 'vocal');
      }
    }

    if (this.lastAccompanimentUrl) {
      if (this.originalAccompanimentBuffer) {
        accompanimentSource = this.createSourceFromBuffer(this.originalAccompanimentBuffer, 'accompaniment');
        console.log('🎵 ⚡ FAST: Using cached accompaniment buffer for play');
      } else {
        console.log('🎵 ⚠️ SLOW: Loading accompaniment from URL for play (buffer not cached)');
        accompanimentSource = await this.loadAudioFromUrl(this.lastAccompanimentUrl, 'accompaniment');
      }
    }

    if (this.lastDrumsUrl) {
      if (this.originalDrumsBuffer) {
        drumsSource = this.createSourceFromBuffer(this.originalDrumsBuffer, 'drums');
        console.log('🎵 ⚡ FAST: Using cached drums buffer for play');
      } else {
        console.log('🎵 ⚠️ SLOW: Loading drums from URL for play (buffer not cached)');
        drumsSource = await this.loadAudioFromUrl(this.lastDrumsUrl, 'drums');
      }
    }

    // Start all tracks simultaneously using the same timestamp
    const startTime = this.audioContext.currentTime + 0.01; // Small delay to ensure sync

    if (vocalSource) {
      // Add onended event handler for vocal track
      vocalSource.onended = () => {
        console.log('🎵 Vocal track ended', { isManualStop: this.isManualStop });
        if (!this.isManualStop) {
          this.vocalEnded = true;
          this.checkIfSongEnded();
        }
      };

      vocalSource.start(startTime, this.seekOffset);
      console.log('🎵 Vocal track scheduled to start at:', startTime, 'offset:', this.seekOffset);
    }

    if (accompanimentSource) {
      // Add onended event handler for accompaniment track
      accompanimentSource.onended = () => {
        console.log('🎵 Accompaniment track ended', { isManualStop: this.isManualStop });
        if (!this.isManualStop) {
          this.accompanimentEnded = true;
          this.checkIfSongEnded();
        }
      };

      accompanimentSource.start(startTime, this.seekOffset);
      console.log('🎵 Accompaniment track scheduled to start at:', startTime, 'offset:', this.seekOffset);
    }

    if (drumsSource) {
      // Add onended event handler for drums track
      drumsSource.onended = () => {
        console.log('🎵 Drums track ended', { isManualStop: this.isManualStop });
        if (!this.isManualStop) {
          this.drumsEnded = true;
          this.checkIfSongEnded();
        }
      };

      drumsSource.start(startTime, this.seekOffset);
      console.log('🎵 Drums track scheduled to start at:', startTime, 'offset:', this.seekOffset);
    }

    if (vocalSource || accompanimentSource || drumsSource) {
      console.log('🎵 All tracks synchronized and started');

      // Start time tracking
      this.startTime = this.audioContext.currentTime;
      this.isPlaying = true;
      this.startTimeTracking();
    }

    if (!this.lastVocalUrl && !this.lastAccompanimentUrl && !this.lastDrumsUrl) {
      console.warn('🎵 No audio URLs available for playback');
    }
  }

  // Pause playback (saves current position)
  pause() {
    if (this.isPlaying) {
      console.log('🎵 Pausing audio playback');

      // Save current position before stopping
      this.pauseTime = this.getCurrentTime();
      this.seekOffset = this.pauseTime;
      console.log(`🎵 Saved pause position: ${this.pauseTime.toFixed(2)}s`);

      // Now stop the audio
      this.stop();
    }
  }

  // Stop both tracks
  stop() {
    console.log('🎵 Stopping audio playback');

    // Set manual stop flag before stopping sources
    this.isManualStop = true;

    // Stop time tracking
    this.isPlaying = false;
    this.stopTimeTracking();

    if (this.vocalSourceNode) {
      try {
        // Clear onended handler before stopping to prevent race conditions
        this.vocalSourceNode.onended = null;
        this.vocalSourceNode.stop();
      } catch (e) {
        // Source might already be stopped
        console.log('🎵 Vocal source already stopped');
      }
      this.vocalSourceNode.disconnect();
      this.vocalSourceNode = null;
    }

    if (this.accompanimentSourceNode) {
      try {
        // Clear onended handler before stopping to prevent race conditions
        this.accompanimentSourceNode.onended = null;
        this.accompanimentSourceNode.stop();
      } catch (e) {
        // Source might already be stopped
        console.log('🎵 Accompaniment source already stopped');
      }
      this.accompanimentSourceNode.disconnect();
      this.accompanimentSourceNode = null;
    }

    if (this.drumsSourceNode) {
      try {
        // Clear onended handler before stopping to prevent race conditions
        this.drumsSourceNode.onended = null;
        this.drumsSourceNode.stop();
      } catch (e) {
        // Source might already be stopped
        console.log('🎵 Drums source already stopped');
      }
      this.drumsSourceNode.disconnect();
      this.drumsSourceNode = null;
    }

    if (this.pitchShiftEngine === PITCH_SHIFT_ENGINE_SIGNALSMITH) {
      this.sharedPitchNode?.schedule({
        output: this.audioContext.currentTime,
        active: false,
      }).catch((error) => {
        console.warn('🎵 Failed to stop Signalsmith processing:', error);
      });
    }
    this.masterLimiterNode?.port?.postMessage({ type: 'reset' });

    // Reset the manual stop flag after a short delay
    // This ensures the onended handlers have time to check it
    setTimeout(() => {
      this.isManualStop = false;
    }, 100);
  }

  // Fast seek using cached buffers (no network requests!)
  async seek(position, vocalUrl, accompanimentUrl, drumsUrl = null, shouldResume = true) {
    console.log(`🎵 FAST SEEK to position: ${position}s, shouldResume: ${shouldResume}`);
    console.log(`🎵 Song ended callback status: ${this.songEndedCallback ? 'registered' : 'NOT registered'}`);

    // Update seek offset for time tracking
    this.seekOffset = position;

    this.stop();

    // Minimal delay - just 10ms instead of 50ms
    await new Promise(resolve => setTimeout(resolve, 10));
    await this.prepareSignalsmithForPlayback();

    // Reset completion tracking
    this.vocalEnded = false;
    this.accompanimentEnded = false;
    this.drumsEnded = false;
    this.songEndHandled = false;

    // Create new source nodes from CACHED buffers (no network requests!)
    let vocalSource = null;
    let accompanimentSource = null;
    let drumsSource = null;

    if (vocalUrl && this.originalVocalBuffer) {
      vocalSource = this.createSourceFromBuffer(this.originalVocalBuffer, 'vocal');
      console.log('🎵 ⚡ FAST: Using cached vocal buffer');
    } else if (vocalUrl) {
      console.log('🎵 ⚠️ SLOW: Loading vocal from URL (buffer not cached)');
      vocalSource = await this.loadAudioFromUrl(vocalUrl, 'vocal');
    }

    if (accompanimentUrl && this.originalAccompanimentBuffer) {
      accompanimentSource = this.createSourceFromBuffer(this.originalAccompanimentBuffer, 'accompaniment');
      console.log('🎵 ⚡ FAST: Using cached accompaniment buffer');
    } else if (accompanimentUrl) {
      console.log('🎵 ⚠️ SLOW: Loading accompaniment from URL (buffer not cached)');
      accompanimentSource = await this.loadAudioFromUrl(accompanimentUrl, 'accompaniment');
    }

    if (drumsUrl && this.originalDrumsBuffer) {
      drumsSource = this.createSourceFromBuffer(this.originalDrumsBuffer, 'drums');
      console.log('🎵 ⚡ FAST: Using cached drums buffer');
    } else if (drumsUrl) {
      console.log('🎵 ⚠️ SLOW: Loading drums from URL (buffer not cached)');
      drumsSource = await this.loadAudioFromUrl(drumsUrl, 'drums');
    }

    // Determine if we should start playing after seek
    const shouldStartPlaying = shouldResume;

    if (shouldStartPlaying && (vocalSource || accompanimentSource || drumsSource)) {
      // Start all tracks simultaneously at the seek position
      const startTime = this.audioContext.currentTime + 0.01;

      if (vocalSource) {
        // Add onended event handler for vocal track - CRITICAL: Preserve callback reference
        vocalSource.onended = () => {
          console.log('🎵 Vocal track ended after seek', {
            isManualStop: this.isManualStop,
            hasCallback: !!this.songEndedCallback,
            position: this.getCurrentTime()
          });
          if (!this.isManualStop) {
            this.vocalEnded = true;
            this.checkIfSongEnded();
          }
        };

        vocalSource.start(startTime, position);
        console.log('🎵 Vocal track seeking to:', position, 'at time:', startTime);
      }

      if (accompanimentSource) {
        // Add onended event handler for accompaniment track - CRITICAL: Preserve callback reference
        accompanimentSource.onended = () => {
          console.log('🎵 Accompaniment track ended after seek', {
            isManualStop: this.isManualStop,
            hasCallback: !!this.songEndedCallback,
            position: this.getCurrentTime()
          });
          if (!this.isManualStop) {
            this.accompanimentEnded = true;
            this.checkIfSongEnded();
          }
        };

        accompanimentSource.start(startTime, position);
        console.log('🎵 Accompaniment track seeking to:', position, 'at time:', startTime);
      }

      if (drumsSource) {
        // Add onended event handler for drums track - CRITICAL: Preserve callback reference
        drumsSource.onended = () => {
          console.log('🎵 Drums track ended after seek', {
            isManualStop: this.isManualStop,
            hasCallback: !!this.songEndedCallback,
            position: this.getCurrentTime()
          });
          if (!this.isManualStop) {
            this.drumsEnded = true;
            this.checkIfSongEnded();
          }
        };

        drumsSource.start(startTime, position);
        console.log('🎵 Drums track seeking to:', position, 'at time:', startTime);
      }

      // Restart time tracking from new position
      this.startTime = this.audioContext.currentTime;
      this.isPlaying = true;
      this.startTimeTracking();
      console.log('🎵 Playback resumed after seek - callback preserved:', !!this.songEndedCallback);
    } else {
      // Just update the position without starting playback
      this.isPlaying = false;
      console.log('🎵 Seek completed, playback remains paused - callback preserved:', !!this.songEndedCallback);
    }
  }

  // Time tracking methods
  startTimeTracking() {
    this.stopTimeTracking(); // Clear any existing interval

    this.timeUpdateInterval = setInterval(() => {
      if (this.isPlaying && this.timeUpdateCallback) {
        const currentTime = this.getCurrentTime();
        this.timeUpdateCallback(currentTime);
      }
    }, 100); // Update every 100ms for smooth progress bar
  }

  stopTimeTracking() {
    if (this.timeUpdateInterval) {
      try {
        clearInterval(this.timeUpdateInterval);
        console.log('🧹 Time tracking interval cleared');
      } catch (error) {
        console.warn('🧹 Error clearing time tracking interval:', error);
      }
      this.timeUpdateInterval = null;
    }
  }

  // Get current playback time in seconds
  getCurrentTime() {
    if (!this.isPlaying || !this.audioContext) {
      return this.seekOffset;
    }

    const elapsed = this.audioContext.currentTime - this.startTime;
    return this.seekOffset + elapsed;
  }

  // Set callback for time updates (for progress bar)
  setTimeUpdateCallback(callback) {
    this.timeUpdateCallback = callback;
  }

  // Get current pitch shift in semitones
  getCurrentPitchShift() {
    return this.currentPitchShift;
  }

  // Check if initialized and supported
  isSupported() {
    return this.isInitialized && this.audioContext && this.audioContext.audioWorklet;
  }

  // Clear stored audio buffers to free memory
  clearBuffers() {
    this.originalVocalBuffer = null;
    this.originalAccompanimentBuffer = null;
    this.originalDrumsBuffer = null;
    this.lastVocalUrl = null;
    this.lastAccompanimentUrl = null;
    this.lastDrumsUrl = null;
    console.log('🧹 Audio buffers cleared from memory');
  }

  // Cleanup method that should be called when switching songs
  cleanupForNewSong() {
    this.stop();
    this.clearBuffers();
    this.seekOffset = 0;
    this.pauseTime = 0;
    this.vocalEnded = false;
    this.accompanimentEnded = false;
    this.drumsEnded = false;
    this.songDuration = 0;
    this.songEndHandled = false;
    // Note: We don't Reset isManualStop here because stop() already handle its own lifecycle
    // and setting it to false immediately here sabotages the protection stop() provides
    // Note: We don't clear songEndedCallback here as it should persist across songs
    console.log('🧹 AudioPitchShifter cleaned up for new song');
  }

  // Cleanup
  destroy() {
    this.stop();
    this.stopTimeTracking();
    this.clearBuffers();

    // Properly close AudioContext
    if (this.audioContext && this.audioContext.state !== 'closed') {
      this.audioContext.close().then(() => {
        console.log('🧹 AudioContext closed successfully');
      }).catch((error) => {
        console.warn('🧹 Error closing AudioContext:', error);
      });
    }

    // Clear all references
    for (const engine of Object.keys(this.pitchNodeSets)) {
      this.disposePitchNodeSet(engine);
    }
    this.pitchNodeSets = {
      [PITCH_SHIFT_ENGINE_BYPASS]: null,
      [PITCH_SHIFT_ENGINE_SIGNALSMITH]: null,
    };
    this.signalsmithFactory = null;
    this.signalsmithLatencySeconds = 0;
    this.audioContext = null;
    this.vocalPitchNode = null;
    this.accompanimentPitchNode = null;
    this.drumsPitchNode = null;
    this.sharedPitchNode = null;
    this.drumsDelayNode = null;
    this.vocalGainNode = null;
    this.accompanimentGainNode = null;
    this.drumsGainNode = null;
    this.masterInputNode = null;
    this.masterGainNode = null;
    this.masterLimiterNode = null;
    this.vocalSourceNode = null;
    this.accompanimentSourceNode = null;
    this.drumsSourceNode = null;
    this.timeUpdateCallback = null;
    this.isInitialized = false;

    console.log('🧹 AudioPitchShifter fully destroyed');
  }

  // Check if all tracks have ended
  checkIfSongEnded() {
    if (this.songEndHandled) {
      console.log('🎵 Song end already handled - ignoring duplicate event');
      return;
    }

    const trackStatuses = [
      { type: 'accompaniment', hasTrack: !!this.lastAccompanimentUrl, ended: this.accompanimentEnded },
      { type: 'vocal', hasTrack: !!this.lastVocalUrl, ended: this.vocalEnded },
      { type: 'drums', hasTrack: !!this.lastDrumsUrl, ended: this.drumsEnded }
    ].filter(track => track.hasTrack);

    if (trackStatuses.length === 0) {
      console.warn('🎵 No active audio tracks detected when checking for song end');
      return;
    }

    const allTracksFinished = trackStatuses.every(track => track.ended);
    const pendingTracks = trackStatuses.filter(track => !track.ended).map(track => track.type);

    console.log('🎵 Checking if song ended (waiting for all tracks):', {
      trackStatuses: trackStatuses.map(track => ({ type: track.type, ended: track.ended })),
      pendingTracks,
      currentTime: this.getCurrentTime(),
      songDuration: this.songDuration,
      hasCallback: !!this.songEndedCallback,
      isPlaying: this.isPlaying
    });

    if (!allTracksFinished) {
      console.log('🎵 Song not yet ended - waiting for remaining tracks to finish', { pendingTracks });
      return;
    }

    console.log('🎵 Song ended detected: all active tracks finished', {
      completedTracks: trackStatuses.map(track => track.type)
    });
    this.songEndHandled = true;
    this.triggerSongEndedCallback();
  }

  // Trigger the song ended callback
  triggerSongEndedCallback() {
    const currentTime = this.getCurrentTime();
    console.log('🎵 triggerSongEndedCallback called', {
      isPlaying: this.isPlaying,
      hasCallback: !!this.songEndedCallback,
      currentTime: currentTime,
      songDuration: this.songDuration,
      timeRemaining: this.songDuration ? (this.songDuration - currentTime) : 'unknown'
    });

    this.isPlaying = false;
    this.stopTimeTracking();

    if (this.songEndedCallback) {
      console.log('🎵 Triggering song ended callback - about to execute');
      // Use setTimeout to avoid any race conditions
      setTimeout(() => {
        console.log('🎵 Executing song ended callback NOW');
        try {
          this.songEndedCallback();
          console.log('🎵 Song ended callback executed successfully');
        } catch (error) {
          console.error('🎵 Error executing song ended callback:', error);
        }
      }, 100);
    } else {
      console.warn('🎵 No song ended callback registered! Auto-advance will not work.');
    }
  }

  // Set callback for when song ends
  setSongEndedCallback(callback) {
    this.songEndedCallback = callback;
    console.log('🎵 Song ended callback registered');
  }

  // Set the song duration for progress tracking
  setSongDuration(duration) {
    this.songDuration = duration;
  }
}

export default AudioPitchShifter;
